import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { TipoFormulaEnum } from '@shared/enums/formula/tipo-formula.enum';
import { TipoArredondamentoEnum } from '@shared/enums/formula/tipo-arredondamento.enum';
import { FormulaCardModoEnum } from '@shared/enums/motor-fiscal/formula-card-modo.enum';
import { FormulaItemStruct } from '@shared/services/structs/formula/formula-item.struct';
import { FormulaCatalogoItemStruct } from '@shared/services/structs/formula/formula-catalogo-item.struct';
import { FormulaModeloStruct } from '@shared/services/structs/formula/formula-modelo.struct';
import { VariavelStruct } from '@shared/services/structs/formula/variavel.struct';
import { modeloAtivo, descreverVigencia, rotuloModeloFormula, VigenciaInfo } from '../utils/formula-modelo.util';
import { AlterarModeloSectionComponent } from '../alterar-modelo-section/alterar-modelo-section.component';
import { FormulaEditorSectionComponent, FormulaEditorSectionValue } from '../formula-editor-section/formula-editor-section.component';
import { FormulaExpressaoSectionComponent } from '../formula-expressao-section/formula-expressao-section.component';
import { classeBadgeVariavel, FORMULA_BADGE_CLASSES, isVariavelParametro } from '../utils/formula-badge.util';

/**
 * Card de fórmula unificado, com dois modos:
 * - 'listagem': exibição read-only; a página proprietária persiste eventual troca global de modelo.
 * - 'editor': edição local, com toggle de personalização, vigência e arredondamento.
 */
@Component({
    selector: 'app-formula-card',
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatIconModule,
        MatDialogModule,
        MatSlideToggleModule,
        MatTooltipModule,
        AlterarModeloSectionComponent,
        FormulaEditorSectionComponent,
        FormulaExpressaoSectionComponent,
    ],
    templateUrl: './formula-card.component.html',
    styleUrl: './formula-card.component.css',
})
export class FormulaCardComponent implements OnChanges {
    public readonly badgeClasses = FORMULA_BADGE_CLASSES;
    @Input({ required: true }) formula!: FormulaCatalogoItemStruct;
    @Input() modo: FormulaCardModoEnum = FormulaCardModoEnum.Listagem;
    @Input() mostrarVigencia = true;
    @Input() mostrarBadgeModelo = true;
    @Input() mostrarCabecalho = true;
    /** Versão física que este card representa em uma sequência cronológica. */
    @Input() modeloExibicao: FormulaModeloStruct | null = null;
    /** Versões fora da vigência atual começam exibindo somente o header de vigência. */
    @Input() recolhivel = false;
    /**
     * Estado atual da fórmula nesta configuração. No editor alimenta a edição local; na listagem,
     * quando informado, o card exibe a origem e a expressão efetivas desse estado (ex.: simulação).
     */
    @Input() estado: FormulaItemStruct | null = null;
    /** Editor: emite o novo estado da fórmula. */
    @Output() estadoChange = new EventEmitter<FormulaItemStruct>();
    /** Listagem: solicita que a página proprietária altere o modelo global da empresa. */
    @Output() modeloEmpresaSelecionado = new EventEmitter<FormulaModeloStruct>();
    /** Permite alterar a preferência global somente quando a página responsável autorizá-la. */
    @Input() permitirAlteracaoModeloEmpresa = false;

    public readonly TipoFormulaEnum = TipoFormulaEnum;
    public readonly TipoArredondamentoEnum = TipoArredondamentoEnum;
    public readonly FormulaCardModoEnum = FormulaCardModoEnum;

    public secaoAlterarAberta = false;
    public estaRecolhido = false;
    public usarPersonalizada = false;
    public expressaoAtual = '';
    public tipoArredondamento: TipoArredondamentoEnum = TipoArredondamentoEnum.Arredondar;
    public casasDecimais = 2;
    public dataVigenciaInicio: string | null = null;
    public dataVigenciaFim: string | null = null;

    constructor(
        private dialog: MatDialog,
    ) { }

    ngOnChanges(changes: SimpleChanges): void {
        if ((this.modo === FormulaCardModoEnum.Editor || changes['estado']) && (changes['estado'] || changes['formula'])) {
            this.sincronizarEstado();
        }
        if (changes['modeloExibicao'] || changes['recolhivel']) {
            this.estaRecolhido = this.recolhivel;
        }
    }

    /** Variável à qual a fórmula pertence (variável de saída/principal). */
    public get variavel(): VariavelStruct | null {
        return this.formula?.variavelPrincipal ?? null;
    }

    /** O estado em edição vale sempre no editor e, na listagem, somente quando a página o informa. */
    private get consideraEstado(): boolean {
        return this.modo === FormulaCardModoEnum.Editor || !!this.estado;
    }

    public classeVariavel(variavel: VariavelStruct): string {
        return classeBadgeVariavel(variavel);
    }

    public readonly isVariavelParametro = isVariavelParametro;

    public get modeloAtual(): FormulaModeloStruct | null {
        if (this.modeloExibicao) return this.modeloExibicao;
        const modelos = this.formula?.modelos ?? [];
        if (modelos.length === 0) return null;

        if (this.consideraEstado) {
            const chaveFormulaModelo = this.estado?.chaveFormulaModelo ?? this.formula.chaveFormulaModeloEmpresa;
            if (chaveFormulaModelo) {
                const encontrado = modeloAtivo({
                    ...this.formula,
                    modelos: modelos.filter(m => m.chaveFormulaModelo === chaveFormulaModelo),
                    chaveFormulaModeloEmpresa: chaveFormulaModelo,
                });
                if (encontrado) return encontrado;
            }
            return modeloAtivo(this.formula);
        }

        return modeloAtivo(this.formula);
    }

    public vigenciaModelo(modelo: FormulaModeloStruct): VigenciaInfo {
        return descreverVigencia(modelo);
    }

    public alternarRecolhimento(): void {
        if (!this.recolhivel) return;
        this.estaRecolhido = !this.estaRecolhido;
    }

    public rotuloModelo(modelo: FormulaModeloStruct): string {
        return rotuloModeloFormula(this.formula, modelo);
    }

    public get modeloAtualId(): string | null {
        return this.modeloAtual?.chaveFormulaModelo ?? null;
    }

    public get vigencia(): VigenciaInfo {
        // Em personalização, o header reflete em tempo real a vigência informada nos
        // campos (e não a do modelo), avisando o usuário a partir de quando a fórmula vale.
        if (this.modo === FormulaCardModoEnum.Editor && this.usarPersonalizada) {
            return descreverVigencia({
                dataVigenciaInicio: this.dataVigenciaInicio,
                dataVigenciaFim: this.dataVigenciaFim,
            } as FormulaModeloStruct);
        }
        return descreverVigencia(this.modeloAtual);
    }

    /**
     * Na listagem cronológica, somente a versão física vigente pode alterar a
     * escolha do modelo. As demais versões são histórico ou agendamento e
     * permanecem estritamente informativas.
     */
    public get modeloExibidoEstaVigente(): boolean {
        if (this.modo !== FormulaCardModoEnum.Listagem || !this.modeloExibicao) return true;
        return this.vigencia.classe === 'vigencia-header--vigente'
            || this.vigencia.classe === 'vigencia-header--permanente';
    }

    /**
     * Id do modelo considerado "padrão" desta configuração: o modelo escolhido pela empresa na
     * listagem de fórmulas (chaveFormulaModeloEmpresa) ou, na ausência dele, o padrão do sistema.
     */
    public get idModeloPadrao(): string | null {
        return this.formula ? modeloAtivo(this.formula)?.chaveFormulaModelo ?? null : null;
    }

    /**
     * Verdadeiro quando o modelo em uso é o "padrão".
     * - editor: o padrão é o modelo escolhido pela empresa (chaveFormulaModeloEmpresa) ou o do sistema.
     * - listagem: o padrão é o modelo padrão do sistema.
     */
    public get modeloAtualEhPadrao(): boolean {
        const modelo = this.modeloAtual;
        if (!modelo) return false;
        return this.modo === FormulaCardModoEnum.Editor
            ? modelo.chaveFormulaModelo === this.idModeloPadrao
            : modelo.isModeloPadraoSistema;
    }

    /** Verdadeiro quando o modelo em uso difere do padrão (ver modeloAtualEhPadrao). */
    public get modeloAlterouPadrao(): boolean {
        return !!this.modeloAtual && !this.modeloAtualEhPadrao;
    }

    public get rotuloPadrao(): string {
        return 'Padrão do sistema';
    }

    public get classePadrao(): string {
        return FORMULA_BADGE_CLASSES.padraoSistema;
    }

    public get mostrarToggle(): boolean {
        return this.modo === FormulaCardModoEnum.Editor && this.formula.isPersonalizavel && !this.secaoAlterarAberta;
    }

    public get mostrarRestaurarFormula(): boolean {
        return this.modo === FormulaCardModoEnum.Editor && this.usarPersonalizada;
    }

    public get mostrarRestaurarPadrao(): boolean {
        return this.modo === FormulaCardModoEnum.Editor && !this.usarPersonalizada && this.modeloAlterouPadrao;
    }

    public get mostrarAlterarModelo(): boolean {
        const familias = new Set((this.formula.modelos ?? []).map(modelo => modelo.chaveFormulaModelo));
        return (this.modo === FormulaCardModoEnum.Editor || this.permitirAlteracaoModeloEmpresa)
            && !this.usarPersonalizada
            && this.modeloExibidoEstaVigente
            && familias.size > 1;
    }

    public toggleAlterarModelo(): void {
        this.secaoAlterarAberta = !this.secaoAlterarAberta;
    }

    public selecionarModelo(modelo: FormulaModeloStruct): void {
        this.expressaoAtual = modelo.expressao ?? '';
        this.tipoArredondamento = modelo.tipoArredondamento ?? TipoArredondamentoEnum.Arredondar;
        this.casasDecimais = this.estado?.casasDecimais ?? 2;

        if (this.usarPersonalizada) {
            this.emitirPersonalizada();
            return;
        }

        // Após escolher o modelo, fecha a seção de troca — mesmo comportamento da listagem
        // ao confirmar a troca, diferindo apenas pela ausência de confirmação no editor.
        this.secaoAlterarAberta = false;

        // Selecionar o modelo padrão (global da empresa ou, na ausência, o padrão do sistema)
        // mantém o estado como Padrao (herança, sem sobrescrever). Qualquer outro modelo é um
        // override explícito desta configuração, refletindo na expressão exibida e no salvamento.
        if (modelo.chaveFormulaModelo === this.idModeloPadrao) {
            this.estadoChange.emit({ chaveFormula: this.formula.chaveFormula, tipo: TipoFormulaEnum.ModeloPadrao });
            return;
        }

        this.estadoChange.emit({
            chaveFormula: this.formula.chaveFormula,
            tipo: TipoFormulaEnum.ModeloAlternativo,
            chaveFormulaModelo: modelo.chaveFormulaModelo,
        });
    }

    /** Listagem: a página proprietária decide se e como persistirá a seleção global. */
    public onModeloEmpresaSelecionado(modelo: FormulaModeloStruct): void {
        this.secaoAlterarAberta = false;
        this.modeloEmpresaSelecionado.emit(modelo);
    }

    public togglePersonalizada(event: MatSlideToggleChange): void {
        this.usarPersonalizada = event.checked;
        if (!event.checked) {
            this.emitirNaoPersonalizada();
            return;
        }

        this.expressaoAtual = this.estado?.expressao ?? this.modeloAtual?.expressao ?? '';
        this.tipoArredondamento = this.estado?.tipoArredondamento ??
            this.modeloAtual?.tipoArredondamento ??
            TipoArredondamentoEnum.Arredondar;
        this.casasDecimais = this.estado?.casasDecimais ?? 2;
        this.dataVigenciaInicio = this.estado?.dataVigenciaInicio ?? this.hojeIso();
        this.dataVigenciaFim = this.estado?.dataVigenciaFim ?? null;
        this.emitirPersonalizada();
    }

    /** Editor: restaura o estado para o modelo padrão do sistema. */
    public restaurarPadrao(): void {
        if (this.modo === FormulaCardModoEnum.Editor) {
            this.usarPersonalizada = false;
            this.secaoAlterarAberta = false;
            this.expressaoAtual = this.modeloAtual?.expressao ?? '';
            this.tipoArredondamento = this.modeloAtual?.tipoArredondamento ?? TipoArredondamentoEnum.Arredondar;
            this.casasDecimais = 2;
            this.dataVigenciaInicio = null;
            this.dataVigenciaFim = null;
            this.estadoChange.emit({ chaveFormula: this.formula.chaveFormula, tipo: TipoFormulaEnum.ModeloPadrao });
            return;
        }

        // Listagem: solicita a restauração do padrão global à página proprietária.
        const padrao = this.formula.modelos?.find(m => m.isModeloPadraoSistema);
        if (!padrao) return;

        const ref = this.dialog.open(ConfirmModalComponent, {
            width: '460px',
            data: {
                title: 'Restaurar modelo padrão',
                description: this.descricaoRestaurarModeloPadrao,
                buttonConfirm: 'Confirmar',
                buttonClose: 'Cancelar',
            },
        });

        ref.afterClosed().subscribe(result => {
            if (!result?.confirm) return;
            this.secaoAlterarAberta = false;
            this.modeloEmpresaSelecionado.emit(padrao);
        });
    }

    /** Editor: descarta a personalização e desliga o toggle. */
    public restaurarFormula(): void {
        this.usarPersonalizada = false;
        this.emitirNaoPersonalizada();
    }

    public onEditorChange(value: FormulaEditorSectionValue): void {
        this.expressaoAtual = value.expressao;
        this.tipoArredondamento = value.tipoArredondamento;
        this.casasDecimais = value.casasDecimais;
        this.dataVigenciaInicio = value.dataVigenciaInicio;
        this.dataVigenciaFim = value.dataVigenciaFim;
        this.emitirPersonalizada();
    }

    private emitirNaoPersonalizada(): void {
        this.expressaoAtual = this.modeloAtual?.expressao ?? '';
        this.tipoArredondamento = this.modeloAtual?.tipoArredondamento ?? TipoArredondamentoEnum.Arredondar;
        this.casasDecimais = 2;
        this.dataVigenciaInicio = null;
        this.dataVigenciaFim = null;

        if (this.modeloAtual?.isModeloPadraoSistema) {
            this.estadoChange.emit({ chaveFormula: this.formula.chaveFormula, tipo: TipoFormulaEnum.ModeloPadrao });
            return;
        }

        this.estadoChange.emit({
            chaveFormula: this.formula.chaveFormula,
            tipo: TipoFormulaEnum.ModeloAlternativo,
            chaveFormulaModelo: this.modeloAtual?.chaveFormulaModelo ?? null,
        });
    }

    private sincronizarEstado(): void {
        this.usarPersonalizada = this.estado?.tipo === TipoFormulaEnum.Personalizada;
        this.expressaoAtual = this.usarPersonalizada
            ? this.estado?.expressao ?? this.modeloAtual?.expressao ?? ''
            : this.modeloAtual?.expressao ?? '';
        this.tipoArredondamento = this.usarPersonalizada
            ? this.estado?.tipoArredondamento ?? this.modeloAtual?.tipoArredondamento ?? TipoArredondamentoEnum.Arredondar
            : this.modeloAtual?.tipoArredondamento ?? TipoArredondamentoEnum.Arredondar;
        this.casasDecimais = this.usarPersonalizada ? this.estado?.casasDecimais ?? 2 : 2;
        this.dataVigenciaInicio = this.usarPersonalizada ? this.estado?.dataVigenciaInicio ?? this.hojeIso() : null;
        this.dataVigenciaFim = this.usarPersonalizada ? this.estado?.dataVigenciaFim ?? null : null;
    }

    private emitirPersonalizada(): void {
        this.estadoChange.emit({
            chaveFormula: this.formula.chaveFormula,
            tipo: TipoFormulaEnum.Personalizada,
            expressao: this.expressaoAtual,
            tipoArredondamento: this.tipoArredondamento,
            casasDecimais: this.casasDecimais,
            dataVigenciaInicio: this.dataVigenciaInicio ?? this.hojeIso(),
            dataVigenciaFim: this.dataVigenciaFim,
        });
    }

    private hojeIso(): string {
        const hoje = new Date();
        const ano = hoje.getFullYear();
        const mes = String(hoje.getMonth() + 1).padStart(2, '0');
        const dia = String(hoje.getDate()).padStart(2, '0');
        return `${ano}-${mes}-${dia}`;
    }

    private get descricaoRestaurarModeloPadrao(): string {
        return 'O modelo padrão do sistema voltará a ser usado para todas as configurações que herdam o modelo global. Configurações com modelo sobrescrito localmente não são afetadas.';
    }
}
